import React, { Fragment, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import { useReactToPrint } from 'react-to-print';
import { DeleteEvent, DownloadEvent, PrintEvent, ZoomEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { classNames, isFunction } from '@philips-internal/oswald-ui-utils';
import { useIsMobileResolution } from '../../../hooks/use-is-mobile-resolution.js';
import { MEDIA_CONTAINER_DOUBLE_CLICK_INTERVAL, MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE, MEDIA_CONTAINER_MIN_SWAP_DISTANCE, MEDIA_CONTAINER_PRINT_PAGE_STYLE, MEDIA_CONTAINER_ZOOM_SCALE, MediaContainerSwapImageStep, MediaContainerZoomMode } from '../media-container-constants.js';
import { downloadImage } from '../media-container-utils.js';
import { DesktopActions } from './components/desktop-actions.js';
import { MobileActions } from './components/mobile-actions.js';
function MediaContainerItem(props) {
    const { currentImage, data, deleteOptionEnabled, downloadOptionEnabled, showControls, printOptionEnabled } = props;
    const animatedDivRef = useRef(null);
    const [firstTouch, setFirstTouch] = useState();
    const [{ x, y }, animateApi] = useSpring(() => ({ x: 0, y: 0 }));
    const [scale, setScale] = useState(MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE);
    const [lastTouchTime, setLastTouchTime] = useState();
    const [isZoomed, setIsZoomed] = useState(false);
    const isMobileResolution = useIsMobileResolution();
    const resetImagePosition = () => {
        animateApi.start({ x: 0, y: 0 });
    };
    const onDrag = ({ cancel, offset: [offsetX, offsetY], pinching }) => {
        if (!isZoomed || pinching) {
            return cancel();
        }
        animateApi.start({
            x: offsetX,
            y: offsetY,
        });
    };
    const onPinch = ({ offset: [distance] }) => {
        resetImagePosition();
        pinchZoom(distance);
    };
    const gestures = useGesture({ onDrag, onPinch }) ?? (() => [null]);
    const pinchZoom = (newScale) => {
        const scale = newScale > MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE ? newScale : MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE;
        setScale(scale);
        setIsZoomed(newScale > MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE);
        if (scale === MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE) {
            return onResetZoom();
        }
        if (scale > MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE) {
            return onZoomIn();
        }
        onZoomOut();
    };
    const zoom = (zoomMode) => {
        if (zoomMode === MediaContainerZoomMode.RESET) {
            setScale(MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE);
            return onResetZoom();
        }
        const isZoomIn = zoomMode === MediaContainerZoomMode.ZOOM_IN;
        setScale(prevScale => {
            const scaleStep = prevScale * MEDIA_CONTAINER_ZOOM_SCALE;
            const newScaleValue = prevScale + (isZoomIn ? scaleStep : -scaleStep);
            const finalScale = newScaleValue > MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE ? newScaleValue : MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE;
            setIsZoomed(finalScale > MEDIA_CONTAINER_IMAGE_DEFAULT_SCALE);
            return finalScale;
        });
        if (isZoomIn) {
            return onZoomIn();
        }
        onZoomOut();
    };
    const printImage = useReactToPrint({
        content: () => animatedDivRef.current,
        pageStyle: MEDIA_CONTAINER_PRINT_PAGE_STYLE,
    });
    const onDelete = () => {
        const { onDelete } = props;
        if (isFunction(onDelete)) {
            onDelete(new DeleteEvent());
        }
    };
    const onDownload = () => {
        const { onDownload } = props;
        if (isFunction(onDownload)) {
            onDownload(new DownloadEvent());
        }
        void downloadImage(currentImage.src);
    };
    const onPrint = () => {
        const { onPrint } = props;
        if (!printImage) {
            return;
        }
        if (isFunction(onPrint)) {
            onPrint(new PrintEvent());
        }
        printImage();
    };
    const onResetZoom = () => {
        const { onResetZoom } = props;
        if (isFunction(onResetZoom)) {
            onResetZoom(new ZoomEvent());
        }
        resetImagePosition();
    };
    const onSwap = (step) => {
        const { swapImage } = props;
        if (isMobileResolution && !firstTouch) {
            return;
        }
        setFirstTouch(undefined);
        setLastTouchTime(undefined);
        zoom(MediaContainerZoomMode.RESET);
        swapImage(step);
    };
    const onZoomIn = () => {
        const { onZoomIn } = props;
        if (isFunction(onZoomIn)) {
            onZoomIn(new ZoomEvent());
        }
    };
    const onZoomOut = () => {
        const { onZoomOut } = props;
        if (isFunction(onZoomOut)) {
            onZoomOut(new ZoomEvent());
        }
        resetImagePosition();
    };
    const onImageAreaClick = () => {
        const { onImageAreaClick } = props;
        if (isFunction(onImageAreaClick)) {
            onImageAreaClick();
        }
    };
    const onTransformElementTouchEnd = (event) => {
        if (!firstTouch || isZoomed) {
            return;
        }
        const endTouch = event.changedTouches[event.changedTouches.length - 1];
        const moveDistance = firstTouch.pageX - endTouch.pageX;
        const isValidMove = moveDistance >= MEDIA_CONTAINER_MIN_SWAP_DISTANCE || moveDistance <= -MEDIA_CONTAINER_MIN_SWAP_DISTANCE;
        if (!isValidMove) {
            return;
        }
        const swapStep = firstTouch.pageX > endTouch.pageX
            ? MediaContainerSwapImageStep.NEXT
            : MediaContainerSwapImageStep.PREVIOUS;
        onSwap(swapStep);
    };
    const onTransformElementTouchStart = (event) => {
        if (!isMobileResolution) {
            return;
        }
        setFirstTouch(event.touches[event.touches.length - 1]);
        /**
         * This is to ensure that the zoom will work properly on double click
         */
        const isDoubleTouch = lastTouchTime && Number(new Date()) - lastTouchTime < MEDIA_CONTAINER_DOUBLE_CLICK_INTERVAL;
        if (isDoubleTouch && event.touches.length === 1) {
            const zoomType = isZoomed ? MediaContainerZoomMode.ZOOM_OUT : MediaContainerZoomMode.ZOOM_IN;
            zoom(zoomType);
        }
        setLastTouchTime(Number(new Date()));
    };
    const getImageAreaClassName = () => {
        return classNames({
            'c-media-container__img-area': true,
            'c-media-container__img-area--zoomed': isZoomed,
        });
    };
    return (React.createElement("div", { className: "c-media-container__item" },
        React.createElement("div", { className: "c-media-container-transform-wrapper" },
            React.createElement(Fragment, null,
                React.createElement(DesktopActions, { currentImage: currentImage, data: data, deleteOptionEnabled: deleteOptionEnabled, downloadOptionEnabled: downloadOptionEnabled, onDelete: onDelete, onDownload: onDownload, onPrint: onPrint, onSwap: onSwap, printOptionEnabled: printOptionEnabled, zoom: zoom }),
                React.createElement("div", { className: getImageAreaClassName(), onClick: () => onImageAreaClick() },
                    React.createElement(animated.div, { ...gestures(), className: "c-media-container-transform-element", onTouchEnd: onTransformElementTouchEnd, onTouchStart: onTransformElementTouchStart, ref: animatedDivRef, style: {
                            left: x,
                            top: y,
                            transform: `scale(${scale})`,
                        } },
                        React.createElement("img", { className: "c-media-container__img", src: currentImage.src }))))),
        React.createElement(MobileActions, { deleteOptionEnabled: deleteOptionEnabled, downloadOptionEnabled: downloadOptionEnabled, onDelete: onDelete, onDownload: onDownload, onPrint: onPrint, printOptionEnabled: printOptionEnabled, showControls: showControls })));
}
export default MediaContainerItem;
//# sourceMappingURL=media-container-item.js.map